import { IconsEnum } from '../../../../../../../assets/icons/types';
import { SimpleModal } from '../../../../../../../helpers/modals/SimpleModal';
import { S3MultipartDropzoneArea } from '../../../../../../../helpers/dropzone/S3MultipartDropzoneArea';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { PureTooltipIconButtonHelper } from '../../../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { StaticImageHelper } from '../../../../../../../helpers/StaticImageHelper';
import { LoadingSpinner } from '../../../../../../../helpers/LoadingSpinner';
import { modelViewersKeys, words } from '../../../../../../../locales/keys';
import { TooltipPlacement } from '../../../../../../../helpers/tooltips/tooltipsConstants';

import { useModelViewerImageBackgroundModal } from './hooks';

interface ModelViewerImageBackgroundModalProps {
  isOpen: boolean;
  backgroundImageUrl: string | null;
  sessionUrls: string[];
  onSelectImage: (url: string) => void;
  onAddSessionUrl: (url: string) => void;
  onRemoveSessionUrl: (url: string) => void;
  onClose: () => void;
}

function ModelViewerImageBackgroundModal({
  isOpen,
  backgroundImageUrl,
  sessionUrls,
  onSelectImage,
  onAddSessionUrl,
  onRemoveSessionUrl,
  onClose
}: ModelViewerImageBackgroundModalProps) {
  const {
    pendingFiles,
    handleFilesAccepted,
    handleFileCreated,
    handleFileUploaded,
    handleFileFailed,
    handleCancelPending,
    handleSelectUploaded,
    handleClose
  } = useModelViewerImageBackgroundModal({
    backgroundImageUrl,
    onSelectImage,
    onAddSessionUrl,
    onClose
  });

  const hasItems = pendingFiles.length > 0 || sessionUrls.length > 0;

  return (
    <SimpleModal
      isOpen={isOpen}
      hideModal={handleClose}
      i18nTitle={modelViewersKeys.backgroundImageModalTitle}
      withoutButtons
    >
      <div className="space-y-4">
        <S3MultipartDropzoneArea
          type="imageCrops"
          onFilesAccepted={handleFilesAccepted}
          onFileCreated={handleFileCreated}
          onFileUploaded={handleFileUploaded}
          onFileFailed={handleFileFailed}
        />

        {hasItems && (
          <div className="grid grid-cols-3 gap-2">
            {pendingFiles.map((f) => (
              <div
                key={f.id}
                className="relative aspect-1 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800"
              >
                <StaticImageHelper
                  src={f.localUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain"
                />

                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <LoadingSpinner size="sm" />
                </div>

                <PureTooltipIconButtonHelper
                  className="absolute top-1 right-1 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                  icon={IconsEnum.X_OUTLINE}
                  iconClassName="w-3 h-3"
                  tooltipI18nText={words.cancel}
                  tooltipPlacement={TooltipPlacement.TOP}
                  onClick={() => handleCancelPending(f.id)}
                />
              </div>
            ))}

            {sessionUrls.map((url) => (
              <div
                key={url}
                className="relative aspect-1 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 group"
              >
                <ButtonHelper
                  className="absolute inset-0 w-full h-full border-2 border-transparent hover:border-blue-500 focus:border-blue-500 focus:outline-none transition-colors"
                  onClick={() => handleSelectUploaded(url)}
                >
                  <StaticImageHelper
                    src={url}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </ButtonHelper>

                <PureTooltipIconButtonHelper
                  className="absolute top-1 right-1 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors opacity-0 group-hover:opacity-100"
                  icon={IconsEnum.X_OUTLINE}
                  iconClassName="w-3 h-3"
                  tooltipI18nText={words.remove}
                  tooltipPlacement={TooltipPlacement.TOP}
                  onClick={() => onRemoveSessionUrl(url)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </SimpleModal>
  );
}

export default ModelViewerImageBackgroundModal;
